export const WORLD_WIDTH = 390;
export const WORLD_HEIGHT = 844;
export const CORE_X = WORLD_WIDTH / 2;
export const CORE_Y = 422;
export const FIXED_STEP = 1 / 60;

export type UpgradeRarity = 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary' | 'mythic';
export type WeaponFamily = 'kinetic' | 'tesla' | 'cryo' | 'utility';
export type CoreId = 'aegis' | 'tempest' | 'cryon' | 'singularity';
export type EnemyKind = 'basic' | 'fast' | 'tank' | 'ranged' | 'protector' | 'warden' | 'devourer';
export type EliteModifier = 'armored' | 'fast' | 'regenerating';
export type XpTier = 'gray' | 'blue' | 'gold' | 'prismatic';
export type RunPhase =
  'combat' | 'level-up' | 'intermission' | 'evolution' | 'paused' | 'victory' | 'defeat';

export interface Vec2 {
  x: number;
  y: number;
}

export interface PlayerState {
  x: number;
  y: number;
  previousX: number;
  previousY: number;
  velocityX: number;
  velocityY: number;
  moveX: number;
  moveY: number;
  speed: number;
  distanceTravelled: number;
}

export interface XpPickupState {
  id: number;
  x: number;
  y: number;
  previousX: number;
  previousY: number;
  value: number;
  tier: XpTier;
  radius: number;
  magnetized: boolean;
  age: number;
}

export interface BossArenaState {
  active: boolean;
  kind: 'warden' | 'devourer' | null;
  centerX: number;
  centerY: number;
  radius: number;
  pulse: number;
}

export interface CoreStats {
  maxHealth: number;
  health: number;
  maxBarrier: number;
  barrier: number;
  damageMultiplier: number;
  attackSpeedMultiplier: number;
  critChance: number;
  critMultiplier: number;
  healthRegen: number;
  armor: number;
  pickupRadius: number;
  xpMultiplier: number;
  cashMultiplier: number;
  cooldownReduction: number;
  areaMultiplier: number;
}

export type CoreAbilitySpec =
  | { type: 'barrier'; power: number; radius: number }
  | { type: 'chain'; power: number; radius: number; targets: number }
  | { type: 'freeze'; power: number; radius: number }
  | { type: 'singularity'; power: number; radius: number };

export interface CoreDefinition {
  id: CoreId;
  name: string;
  subtitle: string;
  rarity: UpgradeRarity;
  unlockLevel: number;
  unlockBlueprintId: string | null;
  color: number;
  accentColor: number;
  abilityName: string;
  abilityDescription: string;
  abilityCooldown: number;
  ability: CoreAbilitySpec;
  modifiers: {
    maxHealth: number;
    damage: number;
    attackSpeed: number;
    armor: number;
    critChance: number;
    cooldownReduction: number;
    area: number;
  };
}

export interface WeaponState {
  family: Exclude<WeaponFamily, 'utility'>;
  enabled: boolean;
  cooldown: number;
  visualLevel: number;
}

export interface StatusState {
  chillStacks: number;
  chilledFor: number;
  frozenFor: number;
  freezeImmunityFor: number;
  staticStacks: number;
  frostbiteFor: number;
}

export interface EnemyState {
  id: number;
  kind: EnemyKind;
  x: number;
  y: number;
  radius: number;
  health: number;
  maxHealth: number;
  shield: number;
  speed: number;
  damage: number;
  rewardCash: number;
  rewardXP: number;
  attackCooldown: number;
  abilityCooldown: number;
  hitFlash: number;
  eliteModifier: EliteModifier | null;
  status: StatusState;
  bossPhase: number;
  bossShielded: boolean;
}

export interface ProjectileState {
  id: number;
  source: 'player' | 'enemy';
  family: 'kinetic' | 'enemy';
  x: number;
  y: number;
  previousX: number;
  previousY: number;
  velocityX: number;
  velocityY: number;
  radius: number;
  damage: number;
  ttl: number;
  pierce: number;
  bounces: number;
  explosionRadius: number;
  hitIds: number[];
}

export type EffectSpec =
  | {
      type: 'core-multiplier';
      stat: 'damage' | 'attackSpeed' | 'maxHealth' | 'xp' | 'cash' | 'area' | 'pickupRadius';
      perRank: number;
    }
  | {
      type: 'core-add';
      stat: 'critChance' | 'critMultiplier' | 'regen' | 'armor' | 'cooldownReduction';
      perRank: number;
    }
  | { type: 'unlock-weapon'; weapon: 'tesla' | 'cryo' }
  | { type: 'weapon-rule'; rule: string; valuePerRank: number }
  | { type: 'utility-rule'; rule: 'gravity' | 'repair' | 'salvage'; valuePerRank: number };

export interface UpgradeDefinition {
  id: string;
  name: string;
  description: string;
  family: WeaponFamily;
  rarity: UpgradeRarity;
  tier: 1 | 2 | 3 | 4;
  maxRank: number;
  requirements: UpgradeRequirement[];
  tags: string[];
  blueprintId: string | null;
  effects: EffectSpec[];
}

export interface UpgradeRequirement {
  upgradeId: string;
  minRank: number;
}

export interface SynergyDefinition {
  id: string;
  name: string;
  description: string;
  requirements: UpgradeRequirement[];
}

export interface EvolutionDefinition {
  id: string;
  name: string;
  description: string;
  synergyId: string;
  requirements: UpgradeRequirement[];
}

export interface EnemyDefinition {
  id: EnemyKind;
  name: string;
  health: number;
  damage: number;
  speed: number;
  radius: number;
  rewardCash: number;
  rewardXP: number;
  attackRange: number;
  attackInterval: number;
  color: number;
  sides: number;
}

export interface WeightedEnemy {
  enemy: Exclude<EnemyKind, 'devourer'>;
  weight: number;
}

export interface WaveDefinition {
  wave: number;
  duration: number;
  spawnInterval: number;
  enemies: WeightedEnemy[];
  elite: boolean;
  boss: boolean;
  miniBoss?: 'warden';
}

export interface ShopDefinition {
  id: ShopUpgradeId;
  name: string;
  description: string;
  baseCost: number;
}

export type ShopUpgradeId =
  'damage' | 'attack-speed' | 'max-health' | 'armor' | 'regeneration' | 'critical';

export interface UpgradeOffer {
  id: string;
  name: string;
  description: string;
  family: WeaponFamily;
  rarity: UpgradeRarity;
  tier: 1 | 2 | 3 | 4;
  maxRank: number;
  path: string;
  currentRank: number;
  nextRank: number;
}

export interface ShopOffer extends ShopDefinition {
  cost: number;
  affordable: boolean;
}

export interface MetaModifiers {
  powerLevel: number;
  integrityLevel: number;
  insightLevel: number;
  unlockedBlueprints: string[];
  selectedCoreId: CoreId;
  coreMasteryLevel: number;
}

export interface RunStats {
  damageDealt: number;
  damageTaken: number;
  kills: number;
  elapsedTime: number;
  maxCombo: number;
}

export interface RunState {
  seed: number;
  tick: number;
  phase: RunPhase;
  phaseBeforePause: Exclude<RunPhase, 'paused'>;
  wave: number;
  waveTime: number;
  spawnCooldown: number;
  waveSpawnFinished: boolean;
  level: number;
  xp: number;
  xpToNext: number;
  pendingLevelUps: number;
  cash: number;
  rerollsRemaining: number;
  evolutionTrigger: boolean;
  evolutionId: string | null;
  pendingEvolutionId: string | null;
  coreId: CoreId;
  coreAbilityCooldown: number;
  core: CoreStats;
  player: PlayerState;
  xpPickups: XpPickupState[];
  bossArena: BossArenaState;
  weapons: WeaponState[];
  upgradeRanks: Record<string, number>;
  shopRanks: Record<ShopUpgradeId, number>;
  synergies: string[];
  enemies: EnemyState[];
  projectiles: ProjectileState[];
  upgradeOffers: UpgradeOffer[];
  shopOffers: ShopOffer[];
  resultReward: number;
  resultProfileXp: number;
  resultCoreXp: number;
  blueprintReward: string | null;
  combo: number;
  comboTimer: number;
  momentum: number;
  overdriveFor: number;
  stats: RunStats;
}

export interface SimulationSnapshot extends RunState {
  fpsHint: number;
}

export type SimulationCommand =
  | { type: 'set-movement'; x: number; y: number }
  | { type: 'select-upgrade'; upgradeId: string; expectedRank: number }
  | { type: 'reroll-upgrades' }
  | { type: 'buy-shop'; shopId: ShopUpgradeId | null }
  | { type: 'acknowledge-evolution' }
  | { type: 'pause' }
  | { type: 'resume' }
  | { type: 'debug-add-xp'; amount: number }
  | { type: 'debug-damage-core'; amount: number }
  | { type: 'debug-next-wave' }
  | { type: 'debug-force-evolution' }
  | { type: 'debug-max-skills' }
  | { type: 'debug-stress' };

export interface GameEventMap {
  enemySpawned: { enemyId: number; kind: EnemyKind };
  enemyDamaged: { enemyId: number; amount: number; critical: boolean; family: WeaponFamily };
  enemyKilled: { enemyId: number; kind: EnemyKind; x: number; y: number };
  xpDropped: { pickupId: number; tier: XpTier; value: number; x: number; y: number };
  xpCollected: { pickupId: number; tier: XpTier; value: number; x: number; y: number };
  coreDamaged: { amount: number; health: number };
  projectileFired: { projectileId: number; family: 'kinetic' | 'enemy' };
  teslaFired: { points: Vec2[] };
  cryoPulse: { radius: number };
  gravityPulse: { radius: number };
  coreAbilityActivated: { coreId: CoreId; name: string; color: number; radius: number };
  systemUpgradeActivated: {
    upgradeId: string;
    name: string;
    rank: number;
    maxRank: number;
    tier: number;
    rarity: UpgradeRarity;
    family: WeaponFamily;
  };
  overdriveActivated: { duration: number };
  levelUp: { level: number };
  waveStarted: { wave: number };
  waveCompleted: { wave: number };
  synergyUnlocked: { synergyId: string; name: string };
  evolutionUnlocked: { evolutionId: string; name: string };
  bossEntered: { enemyId: number; kind: 'warden' | 'devourer'; name: string };
  bossArenaLocked: { kind: 'warden' | 'devourer'; x: number; y: number; radius: number };
  bossArenaReleased: { kind: 'warden' | 'devourer' };
  bossPhase: { phase: number; kind: 'warden' | 'devourer' };
  runEnded: { result: 'victory' | 'defeat'; reward: number };
}

export type GameEvent = {
  [K in keyof GameEventMap]: { type: K; payload: GameEventMap[K] };
}[keyof GameEventMap];
